import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Car,
  CheckCircle2,
  Clock3,
  Lock,
  Package,
  RefreshCw,
  Search,
  ShieldCheck,
  Star,
  Unlock,
  UserRound,
  Utensils,
  X
} from 'lucide-react';
import { API } from '../../context/AuthContext';
import './AdminClients.css';

const STATUS_META = {
  active: {
    label: 'Activo',
    className: 'active'
  },
  temporarily_blocked: {
    label: 'Suspendido',
    className: 'temporary'
  },
  blocked: {
    label: 'Bloqueado',
    className: 'blocked'
  }
};

const BLOCK_REASONS = [
  'Incumplimiento de normas',
  'Actividad sospechosa',
  'Seguridad de la cuenta',
  'Fraude o suplantación',
  'Solicitud del usuario',
  'Otro'
];

const UNBLOCK_REASONS = [
  'Revisión completada',
  'Cumplió el tiempo de suspensión',
  'Apelación aceptada',
  'Bloqueo aplicado por error',
  'Verificación de seguridad completada',
  'Otro'
];

const TEMPORARY_DAYS = [1, 3, 7, 15, 30, 60, 90];

const getStatus = (client) => {
  if (client?.active === false && !client?.account_status) {
    return 'blocked';
  }
  return client?.account_status || 'active';
};

const formatDate = (value) => {
  if (!value) return 'No disponible';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleString('es-EC', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

const readError = (error, fallback) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join('. ') || fallback;
  }

  return detail || error?.message || fallback;
};

const statusMeta = (client) => {
  const status = getStatus(client);
  return STATUS_META[status] || STATUS_META.active;
};

const ClientStatus = ({ client }) => {
  const meta = statusMeta(client);
  return (
    <span className={`provi-client-status ${meta.className}`}>
      {meta.label}
    </span>
  );
};

const Metric = ({ icon: Icon, label, value }) => (
  <div className="provi-client-metric">
    <Icon size={17} />
    <div>
      <span>{label}</span>
      <strong>{value ?? 0}</strong>
    </div>
  </div>
);

const ModalShell = ({ title, subtitle, onClose, children, className = '' }) => (
  <div
    className="provi-clients-modal-backdrop"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}
  >
    <section
      className={`provi-clients-modal ${className}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <header className="provi-clients-modal-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <button
          type="button"
          className="provi-clients-icon-button"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <X size={19} />
        </button>
      </header>
      {children}
    </section>
  </div>
);

const ActivityList = ({ activity }) => {
  const combined = useMemo(() => {
    const rows = [
      ...(activity?.rides || []).map((item) => ({
        type: 'Taxi',
        icon: Car,
        status: item.status || 'Sin estado',
        at: item.created_at,
        detail: item.destination_address || item.destination || 'Viaje Taxi'
      })),
      ...(activity?.delivery_orders || []).map((item) => ({
        type: 'Delivery',
        icon: Package,
        status: item.status || 'Sin estado',
        at: item.created_at,
        detail: item.delivery_address || item.destination || 'Solicitud Delivery'
      })),
      ...(activity?.food_orders || []).map((item) => ({
        type: 'Comida',
        icon: Utensils,
        status: item.status || 'Sin estado',
        at: item.created_at,
        detail: item.restaurant_name || item.local_name || 'Pedido de comida'
      }))
    ];

    return rows
      .sort((left, right) => new Date(right.at || 0) - new Date(left.at || 0))
      .slice(0, 10);
  }, [activity]);

  if (!combined.length) {
    return (
      <div className="provi-clients-empty compact">
        No hay actividad reciente registrada.
      </div>
    );
  }

  return (
    <div className="provi-client-activity-list">
      {combined.map((item, index) => {
        const Icon = item.icon;
        return (
          <article key={`${item.type}-${item.at || index}-${index}`}>
            <div className="provi-client-activity-icon">
              <Icon size={17} />
            </div>
            <div>
              <strong>{item.type}</strong>
              <span>{item.detail}</span>
            </div>
            <div className="provi-client-activity-meta">
              <b>{item.status}</b>
              <span>{formatDate(item.at)}</span>
            </div>
          </article>
        );
      })}
    </div>
  );
};

const ExpandableAuditComment = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const canExpand = text.length > 100;
  return (
    <div className="provi-client-audit-comment-wrap">
      <small className={!canExpand || expanded ? 'expanded' : ''}>{text}</small>
      {canExpand && (
        <button type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
          {expanded ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
      )}
    </div>
  );
};

const AuditList = ({ rows }) => {
  if (!rows?.length) {
    return (
      <div className="provi-clients-empty compact">
        Todavía no hay acciones administrativas sobre esta cuenta.
      </div>
    );
  }

  return (
    <div className="provi-client-audit-list">
      {rows.map((item, index) => (
        <article key={item.id || `${item.action}-${item.created_at}-${index}`}>
          <span className="provi-client-audit-dot" />
          <div>
            <strong>
              {item.action === 'client_blocked'
                ? 'Cuenta bloqueada'
                : item.action === 'client_unblocked'
                  ? 'Cuenta desbloqueada'
                  : item.action === 'client_block_expired'
                    ? 'Suspensión finalizada'
                    : item.action || 'Acción administrativa'}
            </strong>
            <p>{item.reason || 'Sin motivo registrado'}</p>
            {item.comment && <ExpandableAuditComment text={item.comment} />}
          </div>
          <time>{formatDate(item.created_at)}</time>
        </article>
      ))}
    </div>
  );
};

const ClientDetailModal = ({
  detail,
  activity,
  loading,
  onClose,
  onBlock,
  onUnblock
}) => {
  if (!detail && !loading) return null;

  const status = getStatus(detail);
  const isActive = status === 'active';

  return (
    <ModalShell
      title={loading ? 'Cargando cliente…' : detail?.name || 'Perfil del cliente'}
      subtitle={loading ? 'Consultando información real' : detail?.email}
      onClose={onClose}
      className="wide"
    >
      {loading ? (
        <div className="provi-clients-loading large">
          <RefreshCw className="spin" size={24} />
          Cargando perfil y actividad…
        </div>
      ) : (
        <div className="provi-client-detail">
          <section className="provi-client-identity-card">
            <div className="provi-client-avatar large">
              {(detail?.name || detail?.email || 'C').slice(0, 1).toUpperCase()}
            </div>
            <div className="provi-client-identity-copy">
              <div className="provi-client-identity-title">
                <h4>{detail?.name || 'Cliente PROVI'}</h4>
                <ClientStatus client={detail} />
              </div>
              <p>{detail?.email || 'Correo no disponible'}</p>
              <span>{detail?.phone || 'Teléfono no disponible'}</span>
            </div>
            <div className="provi-client-detail-actions">
              {isActive ? (
                <button
                  type="button"
                  className="provi-client-button danger"
                  onClick={() => onBlock(detail)}
                >
                  <Lock size={17} />
                  Bloquear o suspender
                </button>
              ) : (
                <button
                  type="button"
                  className="provi-client-button success"
                  onClick={() => onUnblock(detail)}
                >
                  <Unlock size={17} />
                  Desbloquear
                </button>
              )}
            </div>
          </section>

          <section className="provi-client-detail-grid">
            <div>
              <span>Registro</span>
              <strong>{formatDate(detail?.created_at)}</strong>
            </div>
            <div>
              <span>Estado técnico</span>
              <strong>{status}</strong>
            </div>
            <div>
              <span>Versión de sesión</span>
              <strong>{detail?.session_version ?? 0}</strong>
            </div>
            <div>
              <span>Última actualización</span>
              <strong>{formatDate(detail?.updated_at)}</strong>
            </div>
          </section>

          {!isActive && (
            <section className="provi-client-block-summary">
              <AlertTriangle size={19} />
              <div>
                <strong>
                  {status === 'temporarily_blocked'
                    ? 'Cuenta suspendida temporalmente'
                    : 'Cuenta bloqueada indefinidamente'}
                </strong>
                <span>{detail?.block_reason || 'Sin motivo visible'}</span>
                {detail?.blocked_until && (
                  <small>Hasta: {formatDate(detail.blocked_until)}</small>
                )}
              </div>
            </section>
          )}

          <div className="provi-client-modal-columns">
            <section className="provi-client-detail-section">
              <header>
                <div>
                  <h4>Actividad reciente</h4>
                  <p>Taxi, Delivery y Comida conservados en el historial.</p>
                </div>
              </header>
              <ActivityList activity={activity} />
            </section>

            <section className="provi-client-detail-section">
              <header>
                <div>
                  <h4>Historial administrativo</h4>
                  <p>Bloqueos, desbloqueos y vencimientos.</p>
                </div>
              </header>
              <AuditList rows={activity?.audit} />
            </section>
          </div>
        </div>
      )}
    </ModalShell>
  );
};

const ClientActionModal = ({
  action,
  saving,
  onClose,
  onSubmit
}) => {
  const isBlock = action?.kind === 'block';
  const [blockType, setBlockType] = useState('temporary');
  const [durationDays, setDurationDays] = useState(7);
  const [reason, setReason] = useState(
    isBlock ? BLOCK_REASONS[0] : UNBLOCK_REASONS[0]
  );
  const [comment, setComment] = useState('');
  const [localError, setLocalError] = useState('');

  if (!action) return null;

  const submit = async (event) => {
    event.preventDefault();
    setLocalError('');

    const cleanComment = comment.trim();
    if (cleanComment.length < 10) {
      setLocalError('El comentario debe tener al menos 10 caracteres.');
      return;
    }

    await onSubmit({
      block_type: isBlock ? blockType : undefined,
      duration_days: isBlock && blockType === 'temporary'
        ? Number(durationDays)
        : undefined,
      reason,
      comment: cleanComment
    });
  };

  return (
    <ModalShell
      title={isBlock ? 'Bloquear o suspender cliente' : 'Desbloquear cliente'}
      subtitle={action.client?.name || action.client?.email}
      onClose={saving ? () => {} : onClose}
    >
      <form className="provi-client-action-form" onSubmit={submit}>
        {isBlock && (
          <>
            <div className="provi-client-segmented">
              <button
                type="button"
                className={blockType === 'temporary' ? 'active' : ''}
                onClick={() => setBlockType('temporary')}
              >
                <Clock3 size={17} />
                Temporal
              </button>
              <button
                type="button"
                className={blockType === 'indefinite' ? 'active' : ''}
                onClick={() => setBlockType('indefinite')}
              >
                <Lock size={17} />
                Indefinido
              </button>
            </div>

            {blockType === 'temporary' && (
              <label>
                <span>Duración</span>
                <select
                  value={durationDays}
                  onChange={(event) => setDurationDays(event.target.value)}
                >
                  {TEMPORARY_DAYS.map((days) => (
                    <option key={days} value={days}>
                      {days} {days === 1 ? 'día' : 'días'}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}

        <label>
          <span>Motivo</span>
          <select
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          >
            {(isBlock ? BLOCK_REASONS : UNBLOCK_REASONS).map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Comentario administrativo obligatorio</span>
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={
              isBlock
                ? 'Describe la evidencia o situación que justifica la medida…'
                : 'Explica por qué se reactiva la cuenta…'
            }
            rows={4}
          />
          <small>{comment.trim().length}/1000 caracteres</small>
        </label>

        <div className="provi-client-action-warning">
          <ShieldCheck size={18} />
          <span>
            {isBlock
              ? 'La sesión del cliente será revocada y no podrá usar PROVI.'
              : 'El cliente podrá iniciar sesión nuevamente. La acción quedará auditada.'}
          </span>
        </div>

        {localError && (
          <div className="provi-clients-error compact">{localError}</div>
        )}

        <footer className="provi-client-action-footer">
          <button
            type="button"
            className="provi-client-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={`provi-client-button ${isBlock ? 'danger' : 'success'}`}
            disabled={saving}
          >
            {saving ? (
              <>
                <RefreshCw className="spin" size={17} />
                Guardando…
              </>
            ) : isBlock ? (
              <>
                <Lock size={17} />
                Confirmar bloqueo
              </>
            ) : (
              <>
                <Unlock size={17} />
                Confirmar desbloqueo
              </>
            )}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};

const AdminClients = ({ onToast }) => {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [activity, setActivity] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/admin/clients`, {
        withCredentials: true,
        params: {
          q: appliedSearch || undefined,
          status: status || undefined,
          limit: 100
        }
      });

      const payload = response.data || {};
      setClients(Array.isArray(payload.items) ? payload.items : []);
      setTotal(Number(payload.total || 0));
    } catch (requestError) {
      setError(readError(requestError, 'No se pudo cargar la lista de clientes.'));
      setClients([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, status]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const counts = useMemo(() => ({
    active: clients.filter((client) => getStatus(client) === 'active').length,
    temporary: clients.filter(
      (client) => getStatus(client) === 'temporarily_blocked'
    ).length,
    blocked: clients.filter((client) => getStatus(client) === 'blocked').length
  }), [clients]);

  const openClient = async (client) => {
    setDetail(client);
    setActivity(null);
    setDetailLoading(true);

    try {
      const [detailResponse, activityResponse] = await Promise.all([
        axios.get(`${API}/admin/clients/${client.id}`, {
          withCredentials: true
        }),
        axios.get(`${API}/admin/clients/${client.id}/activity`, {
          withCredentials: true
        })
      ]);

      setDetail(detailResponse.data || client);
      setActivity(activityResponse.data || null);
    } catch (requestError) {
      onToast?.(readError(requestError, 'No se pudo cargar el perfil del cliente.'));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitAction = async (payload) => {
    if (!action?.client?.id || saving) return;

    setSaving(true);

    try {
      const endpoint = action.kind === 'block' ? 'block' : 'unblock';
      const body = action.kind === 'block'
        ? payload
        : {
            reason: payload.reason,
            comment: payload.comment
          };

      const response = await axios.post(
        `${API}/admin/clients/${action.client.id}/${endpoint}`,
        body,
        { withCredentials: true }
      );

      const emailStatus = response.data?.email_status;
      let successMessage = response.data?.message
        || (action.kind === 'block'
          ? 'Cliente bloqueado correctamente.'
          : 'Cliente desbloqueado correctamente.');

      if (emailStatus === 'failed') {
        successMessage += (
          ' La acción se guardó, pero el correo no pudo enviarse.'
        );
      } else if (emailStatus === 'skipped_no_email') {
        successMessage += (
          ' No se envió correo porque el cliente no tiene email.'
        );
      } else if (emailStatus === 'skipped_not_configured') {
        successMessage += (
          ' No se envió correo porque el servicio no está configurado.'
        );
      } else if (emailStatus === 'sent') {
        successMessage += ' Correo enviado.';
      }

      onToast?.(successMessage);

      const affectedId = action.client.id;
      setAction(null);
      await loadClients();

      if (detail?.id === affectedId) {
        await openClient({ ...detail, id: affectedId });
      }
    } catch (requestError) {
      onToast?.(readError(requestError, 'No se pudo completar la acción.'));
    } finally {
      setSaving(false);
    }
  };

  const applySearch = (event) => {
    event.preventDefault();
    setAppliedSearch(searchDraft.trim());
  };

  return (
    <section className="provi-clients-page">
      <div className="provi-clients-kpis">
        <article>
          <UserRound size={21} />
          <div><span>Total real</span><strong>{total}</strong></div>
        </article>
        <article>
          <CheckCircle2 size={21} />
          <div><span>Activos</span><strong>{counts.active}</strong></div>
        </article>
        <article>
          <Clock3 size={21} />
          <div><span>Suspendidos</span><strong>{counts.temporary}</strong></div>
        </article>
        <article>
          <Lock size={21} />
          <div><span>Bloqueados</span><strong>{counts.blocked}</strong></div>
        </article>
      </div>

      <div className="provi-clients-panel">
        <header className="provi-clients-panel-head">
          <div>
            <h3>Clientes reales</h3>
            <p>
              Perfil, actividad, suspensión, bloqueo y desbloqueo con auditoría.
            </p>
          </div>
          <button
            type="button"
            className="provi-client-button"
            onClick={loadClients}
            disabled={loading}
          >
            <RefreshCw className={loading ? 'spin' : ''} size={17} />
            Actualizar
          </button>
        </header>

        <form className="provi-clients-toolbar" onSubmit={applySearch}>
          <label className="provi-clients-search">
            <Search size={18} />
            <input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Buscar por nombre, correo o teléfono"
            />
          </label>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="temporarily_blocked">Suspendidos</option>
            <option value="blocked">Bloqueados</option>
          </select>
          <button type="submit" className="provi-client-button primary">
            Buscar
          </button>
        </form>

        {error && (
          <div className="provi-clients-error">
            <AlertTriangle size={19} />
            <div>
              <strong>No se pudo cargar Clientes</strong>
              <span>{error}</span>
            </div>
            <button type="button" onClick={loadClients}>Reintentar</button>
          </div>
        )}

        {loading ? (
          <div className="provi-clients-loading">
            <RefreshCw className="spin" size={22} />
            Consultando clientes reales…
          </div>
        ) : !clients.length ? (
          <div className="provi-clients-empty">
            <UserRound size={28} />
            <strong>No hay clientes que coincidan con los filtros.</strong>
            <span>Prueba otra búsqueda o cambia el estado.</span>
          </div>
        ) : (
          <div className="provi-clients-table-wrap">
            <table className="provi-clients-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th>Actividad</th>
                  <th>Registro</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const currentStatus = getStatus(client);
                  const isActive = currentStatus === 'active';

                  return (
                    <tr key={client.id}>
                      <td data-label="Cliente">
                        <div className="provi-client-name-cell">
                          <div className="provi-client-avatar">
                            {(client.name || client.email || 'C')
                              .slice(0, 1)
                              .toUpperCase()}
                          </div>
                          <div>
                            <strong>{client.name || 'Cliente PROVI'}</strong>
                            <span>{client.email || 'Sin correo'}</span>
                            <small>{client.phone || 'Sin teléfono'}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Estado">
                        <ClientStatus client={client} />
                        {client.blocked_until && (
                          <small className="provi-client-until">
                            Hasta {formatDate(client.blocked_until)}
                          </small>
                        )}
                      </td>
                      <td data-label="Actividad">
                        <div className="provi-client-inline-metrics">
                          <span><Car size={14} />{client.total_rides || 0}</span>
                          <span><Package size={14} />{client.delivery_orders || 0}</span>
                          <span><Utensils size={14} />{client.food_orders || 0}</span>
                        </div>
                      </td>
                      <td data-label="Registro">
                        <span className="provi-client-date">
                          {formatDate(client.created_at)}
                        </span>
                      </td>
                      <td data-label="Acciones">
                        <div className="provi-client-row-actions">
                          <button
                            type="button"
                            className="provi-client-button small"
                            onClick={() => openClient(client)}
                          >
                            Revisar
                          </button>
                          {isActive ? (
                            <button
                              type="button"
                              className="provi-client-button small danger"
                              onClick={() => setAction({
                                kind: 'block',
                                client
                              })}
                            >
                              <Lock size={15} />
                              Bloquear
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="provi-client-button small success"
                              onClick={() => setAction({
                                kind: 'unblock',
                                client
                              })}
                            >
                              <Unlock size={15} />
                              Desbloquear
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClientDetailModal
        detail={detail}
        activity={activity}
        loading={detailLoading}
        onClose={() => {
          setDetail(null);
          setActivity(null);
        }}
        onBlock={(client) => setAction({ kind: 'block', client })}
        onUnblock={(client) => setAction({ kind: 'unblock', client })}
      />

      <ClientActionModal
        key={action ? `${action.kind}-${action.client?.id}` : 'closed'}
        action={action}
        saving={saving}
        onClose={() => setAction(null)}
        onSubmit={submitAction}
      />
    </section>
  );
};

export default AdminClients;
